import React, { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import {
  MDBContainer,
  MDBCol,
  MDBRow,
  MDBBtn,
  MDBInput,
  MDBCheckbox,
} from 'mdb-react-ui-kit';
import CompanyNameModal from '../Modals/CompanyNameModal';

const API_BASE_URL = "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/auth";

function Register({ onSwitchToLogin, onRegisterSuccess }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company_name: '',
    password: '',
    confirmPassword: '',
  });

  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Company Name Modal States
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [pendingGoogleUser, setPendingGoogleUser] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value,
    });
  };

  // =====================
  // NORMAL REGISTRATION
  // =====================
  const handleRegister = async (e) => {
    e.preventDefault();

    const {
      firstName,
      lastName,
      email,
      company_name,
      password,
      confirmPassword
    } = formData;

    if (!firstName || !lastName || !email || !company_name || !password || !confirmPassword) {
      alert("⚠️ All fields are required");
      return;
    }

    if (password !== confirmPassword) {
      alert("❌ Passwords do not match");
      return;
    }

    const payload = {
      name: `${firstName} ${lastName}`,
      email,
      password,
      company_name,
    };

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE_URL}/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Registration failed");
        return;
      }

      alert("✅ Registration successful! Please login.");
      onSwitchToLogin();

    } catch (err) {
      console.error("🔥 API Error:", err);
      alert("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // =====================
  // GOOGLE SIGNUP - STEP 1: Get User Info
  // =====================
  const googleSignup = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setGoogleLoading(true);

        const userInfoResponse = await fetch(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.access_token}`,
            },
          }
        );

        const userInfo = await userInfoResponse.json();

        // Always show company modal for Google signup
        setPendingGoogleUser({
          token: tokenResponse.access_token,
          userInfo: userInfo
        });
        setShowCompanyModal(true);

      } catch (err) {
        console.error('❌ Google signup error:', err);
        alert('Google signup failed. Please try again.');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error('❌ Google login failed:', error);
      alert('Google login failed. Please try again.');
    },
  });

  // =====================
  // GOOGLE SIGNUP - STEP 2: Complete Auth with Company Name
  // =====================
  const completeGoogleAuth = async (token, userInfo, companyName) => {
    try {
      const backendResponse = await fetch(`${API_BASE_URL}/google-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          email: userInfo.email,
          name: userInfo.name,
          company_name: companyName,
        }),
      });

      const backendData = await backendResponse.json();

      if (!backendResponse.ok) {
        alert(backendData.message || 'Google authentication failed');
        return;
      }

      localStorage.setItem('token', backendData.token);
      localStorage.setItem('user', JSON.stringify(backendData.user));

      alert(`✅ Welcome ${backendData.user.name}!`);
      onRegisterSuccess && onRegisterSuccess();

    } catch (err) {
      console.error('❌ Google auth completion error:', err);
      alert('Authentication failed. Please try again.');
    }
  };

  // =====================
  // HANDLE COMPANY NAME SUBMISSION
  // =====================
  const handleCompanyNameSubmit = async (companyName) => {
    if (!pendingGoogleUser) return;

    await completeGoogleAuth(
      pendingGoogleUser.token,
      pendingGoogleUser.userInfo,
      companyName
    );

    setShowCompanyModal(false);
    setPendingGoogleUser(null);
  };

  return (
    <>
      <MDBContainer fluid className="p-3" style={{ minHeight: '100vh' }}>
        <MDBRow className="justify-content-center align-items-center" style={{ minHeight: '100vh' }}>

          {/* LEFT: IMAGE SECTION */}
          <MDBCol 
            xs="12" 
            md="6" 
            lg="6"
            className="d-none d-md-flex align-items-center justify-content-center"
            style={{ padding: '2rem' }}
          >
            <div style={{ maxWidth: '100%', width: '100%' }}>
              <img
                src="/images/man_in_brown_coat_shopify_ebay_ad.png"
                className="img-fluid"
                alt="Register"
                style={{ 
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  maxHeight: '80vh'
                }}
              />
            </div>
          </MDBCol>

          {/* RIGHT: REGISTER FORM */}
          <MDBCol 
            xs="12" 
            md="6" 
            lg="5" 
            xl="4"
            className="d-flex justify-content-center"
            style={{ padding: '2rem' }}
          >
            <div style={{ width: '100%', maxWidth: '450px' }}>

              {/* LOGO & TITLE */}
              <div className="text-center mb-4">
                <img
                  src="/images/logo-b.png"
                  alt="ThriftLogo"
                  style={{ 
                    height: 'clamp(60px, 10vw, 80px)',
                    marginBottom: '1rem',
                    width: 'auto'
                  }}
                />
                <p className="text-muted" style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>
                  Join us and start selling everywhere
                </p>
              </div>

              {/* GOOGLE SIGNUP BUTTON */}
              <MDBBtn
                className="mb-3 w-100 d-flex align-items-center justify-content-center"
                size="lg"
                style={{
                  backgroundColor: '#fff',
                  color: '#000',
                  border: '1px solid #ddd',
                  padding: 'clamp(0.6rem, 2vw, 0.8rem)',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                }}
                onClick={() => googleSignup()}
                disabled={googleLoading}
              >
                {googleLoading ? (
                  "Signing up..."
                ) : (
                  <>
                    <img
                      src="/images/google-icon.svg"
                      alt="Google"
                      style={{ 
                        width: 'clamp(18px, 3vw, 22px)', 
                        height: 'clamp(18px, 3vw, 22px)', 
                        marginRight: '10px' 
                      }}
                    />
                    Sign up with Google
                  </>
                )}
              </MDBBtn>

              {/* DIVIDER */}
              <div className="divider d-flex align-items-center my-3">
                <p className="fw-bold mx-3 mb-0" style={{ color: '#888', fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}>
                  OR
                </p>
              </div>

              {/* REGISTER FORM */}
              <form onSubmit={handleRegister}>

                {/* NAME FIELDS */}
                <MDBRow className="mb-3">
                  <MDBCol size="6">
                    <MDBInput
                      label="First Name"
                      id="firstName"
                      type="text"
                      size="lg"
                      value={formData.firstName}
                      onChange={handleChange}
                      style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                    />
                  </MDBCol>
                  <MDBCol size="6">
                    <MDBInput
                      label="Last Name"
                      id="lastName"
                      type="text"
                      size="lg"
                      value={formData.lastName}
                      onChange={handleChange}
                      style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                    />
                  </MDBCol>
                </MDBRow>

                <MDBInput
                  label="Email address"
                  id="email"
                  type="email"
                  size="lg"
                  className="mb-3"
                  value={formData.email}
                  onChange={handleChange}
                  style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                />

                <MDBInput
                  label="Company Name"
                  id="company_name"
                  type="text"
                  size="lg"
                  className="mb-3"
                  value={formData.company_name}
                  onChange={handleChange}
                  style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                />

                <MDBInput
                  label="Password"
                  id="password"
                  type="password"
                  size="lg"
                  className="mb-3"
                  value={formData.password}
                  onChange={handleChange}
                  style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                />

                <MDBInput
                  label="Confirm Password"
                  id="confirmPassword"
                  type="password"
                  size="lg"
                  className="mb-3"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
                />

                <MDBCheckbox
                  name="terms"
                  id="terms"
                  label="I agree to the Terms and Conditions"
                  required
                  className="mb-3"
                  style={{ fontSize: 'clamp(0.85rem, 2vw, 0.95rem)' }}
                />

                <MDBBtn
                  type="submit"
                  className="w-100"
                  size="lg"
                  disabled={loading}
                  style={{ 
                    backgroundColor: '#667eea',
                    padding: 'clamp(0.6rem, 2vw, 0.8rem)',
                    fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                  }}
                >
                  {loading ? "Creating Account..." : "Create Account"}
                </MDBBtn>

              </form>

              {/* LOGIN LINK */}
              <div className="text-center mt-3" style={{ fontSize: 'clamp(0.85rem, 2vw, 0.95rem)' }}>
                <p className="mb-0">
                  Already have an account?{' '}
                  <a 
                    href="#!" 
                    onClick={onSwitchToLogin} 
                    style={{ cursor: 'pointer', textDecoration: 'none', fontWeight: '500' }}
                  >
                    Login here
                  </a>
                </p>
              </div>

            </div>
          </MDBCol>

        </MDBRow>
      </MDBContainer>

      {/* ============================================ */}
      {/* COMPANY NAME MODAL */}
      {/* ============================================ */}
      <CompanyNameModal
        show={showCompanyModal}
        userName={pendingGoogleUser?.userInfo?.name || ''}
        onSubmit={handleCompanyNameSubmit}
      />
    </>
  );
}

export default Register;